import { Amount } from '../subscriptionContracts/types';

export interface ProductVariant {
  id: string;
  title: string;
  price: string;
  selectedOptions?: { value: string }[];
  displayName?: string;
  productId?: string;
  productImage?: string;
  image?: {
    url?: string;
    originalSrc?: string;
  };
  product?: {
    id: string;
  };
  contextualPricing?: {
    price: {
      amount: string;
      currencyCode: string;
    };
  };
}
export interface ProductVariantResponse {
  node: {
    id: string;
    title: string;
    price: string;
    selectedOptions?: { value: string }[];
    sellingPlanGroups: {
      edges: {
        node: any;
      }[];
    };
    image?: {
      url?: string;
    };
    contextualPricing?: {
      price: {
        amount: string;
        currencyCode: string;
      };
    };
  };
}

export interface ShopifyVariant {
  id: string;
  title: string;
  price: string;
  displayName: string;
  image?: {
    url: string;
  };

  product: {
    id: string;
    title: string;
    hasOnlyDefaultVariant: boolean;
    status: string;
    featuredImage?: {
      transformedSrc: string;
    } | null;
  };
}

export interface ProductOptions {
  name: string;
  values: string[];
}

export interface Product {
  id: string;
  title: string;
  status?: string;
  handle?: string;
  sectionId?: string;
  priceRangeV2?: {
    minVariantPrice: Amount;
    maxVariantPrice: Amount;
  };
  onlineStorePreviewUrl?: string;
  featuredImage?: {
    transformedSrc: string;
  } | null;
  images?: Array<{
    id: string;
    originalSrc: string;
  }>;
  options?: ProductOptions[];
  variants: ProductVariant[];
  hasOnlyDefaultVariant?: boolean;
  totalInventory?: number;
  tracksInventory?: boolean;
  productType?: string;
  loaded?: boolean;
}

export interface ProductResponse {
  id: string;
  title: string;
  status?: string;
  priceRangeV2?: {
    minVariantPrice: Amount;
    maxVariantPrice: Amount;
  };
  onlineStorePreviewUrl?: string;
  featuredImage?: {
    transformedSrc: string;
  } | null;
  options?: ProductOptions[];
  variants: { edges: ProductVariantResponse[] };
  hasOnlyDefaultVariant?: boolean;
  totalInventory?: number;
  tracksInventory?: boolean;
}

export interface ProductsResponse {
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  edges: {
    cursor: string;
    node: ProductResponse;
  }[];
}

export interface FlattenProduct extends ProductVariant {
  isGrouper: boolean;
  hasParent: boolean;
  parent?: Product;
  imageSrc?: string;
  child: string[];
  productType?: string;
  status?: string;
  bundleType?: BundleTypes;
  customAttributes?: { key: string; value: string }[];
  product?: {
    id: string;
  };
}

export interface DiscountedFlattenProduct extends FlattenProduct {
  discountedPrice?: string;
  discountType?: string;
  discountAmount?: number;
}

export interface SelectedItems {
  [key: string]: FlattenProduct;
}

export interface SelectedDiscountedFlattenProducts {
  [key: string]: DiscountedFlattenProduct;
}

export interface PricingPolicy {
  __typename?: string;
  basePrice: {
    amount: string;
    fixedValue?: string;
    currencyCode: string;
  };
  cycleDiscounts: CycleDiscounts[];
}

export enum AdjustmentTypes {
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  PERCENTAGE = 'PERCENTAGE',
  PRICE = 'PRICE',
}

export const discountTypeLookUp = {
  percentage: AdjustmentTypes.PERCENTAGE,
  fixed: AdjustmentTypes.FIXED_AMOUNT,
};

export interface CycleDiscountPercentage {
  __typename?: string;
  adjustmentType: AdjustmentTypes.PERCENTAGE;
  adjustmentValue: {
    percentage: number;
  };
  afterCycle: number;
  computedPrice: {
    amount: string;
    currencyCode: string;
  };
}
export interface CycleDiscountFixed {
  __typename?: string;
  adjustmentType: AdjustmentTypes.FIXED_AMOUNT;
  adjustmentValue: {
    amount: string;
    fixedValue?: string;
    currencyCode: string;
  };
  afterCycle: number;
  computedPrice: {
    amount: string;
    currencyCode: string;
  };
}
export interface CycleDiscountPrice {
  __typename?: string;
  adjustmentType: AdjustmentTypes.PRICE;
  adjustmentValue: {
    amount: string;
    fixedValue?: string;
    currencyCode: string;
  };
  afterCycle: number;
  computedPrice: {
    amount: string;
    currencyCode: string;
  };
}

export type CycleDiscounts =
  | CycleDiscountPercentage
  | CycleDiscountFixed
  | CycleDiscountPrice;

interface CycleDiscountApiFixed {
  adjustmentType: AdjustmentTypes.FIXED_AMOUNT;
  afterCycle: number;
  adjustmentValue: {
    fixedValue: number;
  };
  computedPrice: number;
}
interface CycleDiscountApiPercentage {
  adjustmentType: AdjustmentTypes.PERCENTAGE;
  afterCycle: number;
  adjustmentValue: {
    percentage: number;
  };
  computedPrice: number;
}
interface CycleDiscountApiPrice {
  adjustmentType: AdjustmentTypes.PRICE;
  afterCycle: number;
  adjustmentValue: {
    fixedValue: number;
  };
  computedPrice: number;
}

export type CycleDiscountsApi =
  | CycleDiscountApiPercentage
  | CycleDiscountApiFixed
  | CycleDiscountApiPrice;

export interface PricingPolicyApi {
  basePrice: number;
  cycleDiscounts: CycleDiscountsApi[];
}
